import { useState, useEffect, useCallback, useMemo } from 'react';
import { pushApi } from '../utils/pushApi';
import { pushPlatform } from '../utils/pushPlatform';
import { useFeature } from './useFeature';

const SERVICE_WORKER_WAIT_MS = 10000;

const MESSAGES = {
  dismissed: "Notifications weren't turned on. Choose Allow when your browser asks, then try again.",
  blocked: 'Notifications are blocked for this site. Allow them in your browser or phone settings, then try again.',
  noWorker: "This browser couldn't turn notifications on. Try again, or use another browser.",
  server: "We couldn't save this device. Please try again.",
  setType: "We couldn't save that change. Please try again.",
  removeDevice: "We couldn't remove that device. Please try again.",
};

// Re-registering this device is done once per page load, however many
// components use the hook
let resyncedThisLoad = false;
export const resetPushResync = () => { resyncedThisLoad = false; };

/**
 * Converts a base64url-encoded VAPID public key to the Uint8Array
 * format required by pushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

// The service worker, or null if it isn't ready in time
function serviceWorkerReady() {
  let timer;
  const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve(null), SERVICE_WORKER_WAIT_MS); });
  return Promise.race([navigator.serviceWorker.ready, timeout]).finally(() => clearTimeout(timer));
}

const setSwitch = (groups, key, on) =>
  groups.map((g) => ({ ...g, types: g.types.map((t) => (t.key === key ? { ...t, on } : t)) }));

/**
 * Manages the Web Push subscription lifecycle, the notification types and
 * switches, and the person's devices.
 *
 * Returns:
 *  - supported: boolean, false on browsers that can't do push
 *  - platform: { kind, browser, ios } from pushPlatform, for choosing the screen
 *  - permissionState: 'default' | 'granted' | 'denied'
 *  - isSubscribed: boolean
 *  - checked: boolean, true once the first look for an existing subscription is
 *    done (straight away when push isn't supported), so screens can wait for it
 *  - loading: boolean
 *  - error: a plain message, or null
 *  - subscribe({ onGranted }), unsubscribe(), sendTest()
 *  - types: groups of notification types with their switches (feature on only)
 *  - setType(key, enabled)
 *  - devices, removeDevice(id) (feature on only)
 *  - reload(): fetch types and devices again
 *  - preferences, updatePreference(type, enabled): the old shape, for PushNotificationSettings
 */
export function usePushNotifications() {
  const featureOn = useFeature('notifications');
  const supported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  const [permissionState, setPermissionState] = useState(
    supported ? Notification.permission : 'denied'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [checkDone, setCheckDone] = useState(false);
  const [preferences, setPreferences] = useState({ SESSION_REMINDER: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [types, setTypes] = useState([]);
  // As the server lists them, endpoints included; only `devices` below is returned
  const [deviceRows, setDeviceRows] = useState([]);
  // Kept out of what the hook returns: it identifies this device to the push service
  const [currentEndpoint, setCurrentEndpoint] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const platform = useMemo(() => pushPlatform(), [permissionState, supported]);

  // On mount, check for an existing subscription. If there is one, send it to
  // the server again (once per page load): that refreshes when the device was
  // last seen and repairs a device the server has forgotten.
  useEffect(() => {
    if (!supported) return undefined;
    let cancelled = false;
    // Waits at most 10 seconds for a worker, so `checked` still turns true with none registered
    serviceWorkerReady().then((reg) => (reg ? reg.pushManager.getSubscription() : null)).then((sub) => {
      if (cancelled) return;
      setIsSubscribed(!!sub);
      setCheckDone(true);
      setCurrentEndpoint(sub ? sub.endpoint : null);
      if (sub && !resyncedThisLoad) {
        Promise.resolve(pushApi.subscribe(sub.toJSON()))
          .then(() => { resyncedThisLoad = true; })
          .catch(() => {});
      }
    }).catch(() => { if (!cancelled) setCheckDone(true); });
    return () => { cancelled = true; };
  }, [supported]);

  // Fetch current preferences from the server when subscribed
  useEffect(() => {
    if (!isSubscribed) return;
    pushApi.getPreferences()
      .then((res) => setPreferences(res.data))
      .catch(() => {});
  }, [isSubscribed]);

  const reload = useCallback(async () => {
    if (!featureOn) return;
    const [t, d] = await Promise.allSettled([pushApi.getTypes(), pushApi.getDevices()]);
    if (t.status === 'fulfilled') setTypes(t.value.data.groups || []);
    if (d.status === 'fulfilled') setDeviceRows(d.value.data || []);
  }, [featureOn]);

  // Worked out at render, so it is right whichever finishes first: the device
  // list or the look for this device's subscription
  const devices = useMemo(() => deviceRows.map(({ endpoint, ...device }) => ({
    ...device,
    thisDevice: !!endpoint && endpoint === currentEndpoint,
  })), [deviceRows, currentEndpoint]);

  useEffect(() => {
    if (featureOn) reload();
  }, [featureOn, reload]);

  // onGranted: called as soon as the browser's permission is given, before
  // the slower set-up (service worker, push service, saving the device)
  const subscribe = useCallback(async (opts) => {
    if (!supported) return;
    setLoading(true);
    setError(null);
    try {
      const permission = await Notification.requestPermission();
      setPermissionState(permission);
      if (permission !== 'granted') {
        setError(permission === 'denied' ? MESSAGES.blocked : MESSAGES.dismissed);
        return;
      }
      if (typeof opts?.onGranted === 'function') opts.onGranted();

      const reg = await serviceWorkerReady();
      if (!reg) { setError(MESSAGES.noWorker); return; }

      let sub;
      try {
        const { data } = await pushApi.vapidKey();
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      } catch (err) {
        setError(MESSAGES.noWorker);
        return;
      }

      try {
        await pushApi.subscribe(sub.toJSON());
      } catch (err) {
        setError(MESSAGES.server);
        return;
      }
      setCurrentEndpoint(sub.endpoint);
      setIsSubscribed(true);
      reload();
    } finally {
      setLoading(false);
    }
  }, [supported, reload]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    setError(null);
    let sub = null;
    try {
      const reg = await serviceWorkerReady();
      if (!reg) { setError(MESSAGES.noWorker); return; }
      sub = await reg.pushManager.getSubscription();
      if (sub) {
        try {
          await pushApi.unsubscribe(sub.endpoint);
        } catch (err) {
          // The server already forgot this device: that's what we wanted
          if (err?.response?.status !== 404) throw err;
        }
      }
    } catch (err) {
      setError(MESSAGES.server);
    } finally {
      // Always drop the browser's subscription, so notifications really stop
      if (sub) { try { await sub.unsubscribe(); } catch (e) { /* already gone */ } }
      setLoading(false);
    }
    if (sub) {
      setCurrentEndpoint(null);
      setIsSubscribed(false);
      reload();
    }
  }, [reload]);

  const sendTest = useCallback(() => pushApi.sendTest(), []);

  const setType = useCallback(async (key, enabled) => {
    setError(null);
    setTypes((prev) => setSwitch(prev, key, enabled));
    try {
      await pushApi.setType(key, enabled);
    } catch (err) {
      setTypes((prev) => setSwitch(prev, key, !enabled));
      setError(MESSAGES.setType);
    }
  }, []);

  const removeDevice = useCallback(async (id) => {
    setError(null);
    try {
      await pushApi.removeDevice(id);
      setDeviceRows((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      setError(MESSAGES.removeDevice);
    }
  }, []);

  const updatePreference = useCallback(async (type, enabled) => {
    try {
      await pushApi.setType(type, enabled);
      setPreferences((prev) => ({ ...prev, [type]: enabled }));
    } catch (err) {
      setError(MESSAGES.setType);
    }
  }, []);

  return {
    supported,
    platform,
    permissionState,
    isSubscribed,
    checked: !supported || checkDone,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTest,
    types,
    setType,
    devices,
    removeDevice,
    reload,
    preferences,
    updatePreference,
  };
}
